import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'

/**
 * Account Management service
 * Handles finance modules, account settings, and account templates
 */

export interface FinanceModule {
  id: number
  lookupNameEn: string
  lookupNameAr: string
  [key: string]: any
}

export interface Account {
  id: number
  accountId?: number
  accountCode?: string
  accountName?: string
  accountNameEn?: string
  accountNameAr?: string
  [key: string]: any
}

export interface TransactionAccountVm {
  id: number
  transactionModuleId: number
  transactionTypeIdD: number
  debitAccountId: number
  creditAccountId: number
  branchId: number
  ipAddress?: string
  createdById?: number
  createdDate?: string
  updatedById?: number
  updatedDate?: string
}

export interface Category {
  id: number
  lookupCodeId: number
  lookupNameEn: string
  lookupNameAr: string
  isActive: number
  parentCode: number
  createdById?: number
  createdDate?: string
  updatedById?: number
  updatedDate?: string
  companyId?: number
  branchId?: number
}

export interface AccountSettingResponse {
  transactionModuleId: number
  accountTemplate: TransactionAccountVm[]
  categories: Category[]
}

export class AccountManagementService {
  /**
   * Get all finance modules (master data)
   */
  async getMaster(): Promise<FinanceModule[]> {
    const response = await apiClient.get<FinanceModule[]>(endpoints.accountManagement.getMaster)
    return Array.isArray(response.data) ? response.data : []
  }

  /**
   * Get all accounts for a branch
   */
  async getAllAccounts(branchId: number): Promise<Account[]> {
    try {
      const response = await apiClient.get<any>(endpoints.accountManagement.getAllAccounts(branchId))
      const payload = response.data
      
      console.log('🔵 [GetAllAccounts] Full Response:', response)
      console.log('🔵 [GetAllAccounts] Response Data:', payload)
      
      // Helper to map account data
      const mapAccount = (acc: any): Account => {
        return {
          id: acc.id || acc.accountId || 0,
          accountId: acc.accountId || acc.id || 0,
          accountCode: acc.accountCode || acc.code || '',
          accountName: acc.accountName || acc.accountNameEn || acc.nameEn || acc.name || '',
          accountNameEn: acc.accountNameEn || acc.nameEn || acc.name || '',
          accountNameAr: acc.accountNameAr || acc.nameAr || '',
        }
      }
      
      // Handle different response formats
      if (Array.isArray(payload)) {
        return payload.map(mapAccount)
      }
      
      if (payload && typeof payload === 'object') {
        // Check if wrapped in success/data structure
        if ('success' in payload && 'data' in payload) {
          const data = payload.data
          if (Array.isArray(data)) {
            return data.map(mapAccount)
          }
        }
        
        // Check if data is in 'data' property
        if ('data' in payload && Array.isArray(payload.data)) {
          return payload.data.map(mapAccount)
        }
        
        // Check if it's a single account object
        if ('id' in payload || 'accountId' in payload) {
          return [mapAccount(payload)]
        }
      }
      
      console.warn('⚠️ [GetAllAccounts] Unexpected response format:', payload)
      return []
    } catch (error: any) {
      console.error('❌ [GetAllAccounts] Error:', error)
      console.error('❌ [GetAllAccounts] Error Response:', error.response)
      throw error
    }
  }

  /**
   * Get account setting by module ID and branch ID
   */
  async getAccountSettingById(id: number, branchId: number): Promise<AccountSettingResponse> {
    const response = await apiClient.get<AccountSettingResponse>(
      endpoints.accountManagement.getAccountSettingById(id, branchId)
    )
    return response.data
  }

  /**
   * Save account setting
   */
  async saveAccountSetting(data: TransactionAccountVm[]): Promise<any> {
    const response = await apiClient.post(
      endpoints.accountManagement.saveAccountSetting,
      data
    )
    return response.data
  }
}

// Export singleton instance
export const accountManagementService = new AccountManagementService()

